// src/pages/OffStreetAddPage.jsx
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiCall } from "@/api/apiCall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner"; // ya jo bhi toast lib project mein use ho rahi ho

// ── Har entity ke fields yahan define hain. Exact keys/labels apne API ke
// hisaab se adjust kar lena — abhi flatten data structure se guess kiye hain ──
const ENTITY_CONFIG = {
  country: {
    label: "Country",
    apiPath: "/cp-map/off-street/country", // TODO: confirm actual endpoint
    fields: [
      { key: "country", label: "Country Code", required: true },
      { key: "country_name", label: "Country Name", required: true },
    ],
  },
  city: {
    label: "City",
    apiPath: "/cp-map/off-street/city",
    fields: [
      { key: "country", label: "Country Code", required: true },
      { key: "city_cd", label: "City Code", required: true },
      { key: "city_name", label: "City Name", required: true },
    ],
  },
  ops: {
    label: "Ops",
    apiPath: "/cp-map/off-street/ops",
    fields: [
      { key: "country", label: "Country Code", required: true },
      { key: "city_cd", label: "City Code", required: true },
      { key: "ops", label: "Ops Code", required: true },
      { key: "ops_name", label: "Ops Name", required: true },
    ],
  },
  location: {
    label: "Location",
    apiPath: "/cp-map/off-street/location",
    fields: [
      { key: "ops", label: "Ops Code", required: true },
      { key: "city_cd", label: "City Code", required: true },
      { key: "location", label: "Location Name", required: true },
    ],
  },
  gate: {
    label: "Gate",
    apiPath: "/cp-map/off-street/gate",
    fields: [
      { key: "ops", label: "Ops Code", required: true },
      { key: "location", label: "Location", required: true },
      { key: "gate_id", label: "Gate ID", required: true },
      { key: "gate_name", label: "Gate Name", required: true },
    ],
  },
  device: {
    label: "Device",
    apiPath: "/cp-map/off-street/device",
    fields: [
      { key: "gate_id", label: "Gate ID", required: true },
      { key: "device_id", label: "Device ID", required: true },
      { key: "device_name", label: "Device Name", required: true },
      { key: "device_type", label: "Device Type", required: false },
      { key: "latitude", label: "Latitude", required: false },
      { key: "longitude", label: "Longitude", required: false },
    ],
  },
};

const ENTITY_TYPES = Object.keys(ENTITY_CONFIG); // ["country","city","ops","location","gate","device"]

const OffStreetAddPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialType = searchParams.get("type") || "country";
  const [entityType, setEntityType] = useState(
    ENTITY_CONFIG[initialType] ? initialType : "country"
  );
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const config = ENTITY_CONFIG[entityType];

  // ── Jab dropdown se entity type badle, form reset karo aur URL bhi update karo ──
  const handleTypeChange = (value) => {
    setEntityType(value);
    setFormData({});
    setSearchParams({ type: value });
  };

  const handleFieldChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    for (const field of config.fields) {
      if (field.required && !String(formData[field.key] || "").trim()) {
        toast.error(`${field.label} is required`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      const res = await apiCall("post", config.apiPath, formData, null, false, false);
      if (res?.success) {
        toast.success(`${config.label} successfully added`);
        navigate("/off-street-master-data");
      } else {
        toast.error(res?.message || "");
      }
    } catch (err) {
      console.error("Add entity failed:", err);
      toast.error("adding failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-var(--header-height,64px))] overflow-auto p-2 sm:p-3">
      {/* ── Full-width card — max-w hata diya taake screen ke hisaab se stretch ho ── */}
      <Card className="w-full flex-1">
        <CardHeader className="border-b pb-4">
          <Button
            variant="ghost"
            size="sm"
            className="w-fit -ml-2 mb-2 gap-1.5 text-muted-foreground"
            onClick={() => navigate("/off-street-master-data")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <CardTitle className="text-lg sm:text-xl">Add New — {config.label}</CardTitle>
          <CardDescription>
            Select a type below and fill in the required fields.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-5">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* ── Entity type selector — apni alag row mein, chhota rakha hai ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              <div className="flex flex-col gap-1.5">
                <Label>Add Type</Label>
                <Select value={entityType} onValueChange={handleTypeChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {ENTITY_CONFIG[t].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ── Dynamic fields — responsive grid: mobile 1, tablet 2, desktop 3 per line ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {config.fields.map((field) => (
                <div key={field.key} className="flex flex-col gap-1.5">
                  <Label htmlFor={field.key}>
                    {field.label}
                    {field.required && <span className="text-red-500 ml-0.5">*</span>}
                  </Label>
                  <Input
                    id={field.key}
                    value={formData[field.key] || ""}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    placeholder={field.label}
                    className="w-full"
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 mt-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/off-street-master-data")}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="gap-1.5">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default OffStreetAddPage;